import React from "react";
import { Document, Page, StyleSheet } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import type { MiContenidoMetricsExportData } from "../../miContenidoExportData";
import { isFullCalendarMonth } from "../../dates";
import { showProgramPageChannelNames } from "../../programPages/sortProgramPagesForExport";
import {
  METRICS_PDF_COVER_PADDING_BOTTOM,
  METRICS_PDF_COVER_PADDING_TOP,
  METRICS_PDF_PAGE_PADDING_HORIZONTAL,
  METRICS_PDF_PAGE_SIZE,
} from "../constants";
import {
  formatFooterDate,
  formatLongDate,
  formatPeriodLabel,
  formatShortPeriodLabel,
} from "../formatHelpers";
import { MetricsPdfCoverPage } from "../MetricsPdfCoverPage";
import { MetricsPdfDeckPage } from "../MetricsPdfDeckPage";
import { sanitizePdfDisplayText } from "../sanitizePdfDisplayText";
import { syncPdfTheme, usePdfTheme } from "../PdfThemeContext";
import { PDF_FONT_FAMILY, type PdfColorMode } from "../pdfTheme";
import { registerMetricsPdfFonts } from "../registerMetricsPdfFonts";
import {
  buildCommercialEpisodeGroups,
  buildCommercialViewsRows,
  isEmprendeProgramName,
} from "./buildCommercialViewsData";
import { CommercialEpisodesPdfSection } from "./CommercialEpisodesPdfSection";
import { commercialEpisodePages } from "./commercialEpisodePageLayout";
import { CommercialViewsPdfSection } from "./CommercialViewsPdfSection";

registerMetricsPdfFonts();

function CommercialCoverPage({
  branding,
  periodLabel,
  audienceDemoPlaceholder,
}: {
  branding: MiContenidoMetricsExportData["branding"];
  periodLabel: string;
  audienceDemoPlaceholder?: boolean;
}): React.ReactElement {
  const { t } = useTranslation("metricsPdf");
  const theme = usePdfTheme();
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        coverPage: {
          flexDirection: "column",
          backgroundColor: theme.pageBg,
          paddingTop: METRICS_PDF_COVER_PADDING_TOP,
          paddingBottom: METRICS_PDF_COVER_PADDING_BOTTOM,
          paddingHorizontal: METRICS_PDF_PAGE_PADDING_HORIZONTAL,
          fontFamily: PDF_FONT_FAMILY,
          fontSize: 13,
          color: theme.textBody,
        },
      }),
    [theme],
  );

  return (
    <Page size={METRICS_PDF_PAGE_SIZE} style={styles.coverPage} wrap={false}>
      <MetricsPdfCoverPage
        branding={branding}
        periodLabel={periodLabel}
        generated={formatLongDate()}
        title={t("youtubeProgramsReport")}
        {...(branding.podcastCoverUrl || branding.companyLogoUrl
          ? {
              coverImageUrl:
                branding.podcastCoverUrl || branding.companyLogoUrl,
            }
          : {})}
        {...(audienceDemoPlaceholder ? { audienceDemoPlaceholder: true } : {})}
      />
    </Page>
  );
}

type Props = {
  data: MiContenidoMetricsExportData;
  /** Whole-channel long-form YouTube views for the report period. */
  channelViews: number | null;
  colorMode?: PdfColorMode;
  /** Sidebar range — export `period` can be a product floor, not the pick. */
  periodStart?: string;
  periodEnd?: string;
};

export function CommercialViewsPdfDocument({
  data,
  channelViews,
  colorMode = "light",
  periodStart,
  periodEnd,
}: Props): React.ReactElement {
  const { t } = useTranslation("metricsPdf");
  // Must run before children render — react-pdf often skips React context.
  syncPdfTheme(colorMode);

  const { branding, period } = data;
  const startDate = periodStart || period.startDate;
  const endDate = periodEnd || period.endDate;
  const rows = buildCommercialViewsRows(data);
  const episodePages = commercialEpisodePages(
    buildCommercialEpisodeGroups(data),
  );
  const isMonth = isFullCalendarMonth(startDate, endDate);
  const coverPeriodLabel = formatPeriodLabel(startDate, endDate);
  const periodLabel = formatShortPeriodLabel(startDate, endDate);
  const showChannelNames = showProgramPageChannelNames(data.programPages);
  const deckProps = { branding, generated: formatFooterDate() };
  // Emprende is deselected by default but can be added back from the sidebar.
  const emprendeNote = rows.some((row) => isEmprendeProgramName(row.name))
    ? ""
    : t("emprendeExcluded");

  return (
    <Document>
      <CommercialCoverPage
        branding={branding}
        periodLabel={coverPeriodLabel}
        {...(data.audienceDemoPlaceholder
          ? { audienceDemoPlaceholder: true }
          : {})}
      />
      <MetricsPdfDeckPage
        {...deckProps}
        headerTitle={t("viewsByProgram")}
        headerPeriodLabel={periodLabel}
        footerNote={t("youtubeLongFormFooter", {
          period: formatPeriodLabel(startDate, endDate),
          emprendeNote,
        })}
      >
        <CommercialViewsPdfSection
          rows={rows}
          channelViews={channelViews}
          periodLabel={periodLabel}
          channelLabel={isMonth ? t("channelFullMonth") : t("channelPeriod")}
          totalViewsColumnLabel={
            isMonth ? t("monthlyViews") : t("periodViews")
          }
          showChannelNames={showChannelNames}
        />
      </MetricsPdfDeckPage>

      {episodePages.map((page, i) => (
        <MetricsPdfDeckPage
          key={`${page.programId}-${page.pageIndex}-${i}`}
          {...deckProps}
          headerTitle={sanitizePdfDisplayText(page.name)}
          headerPeriodLabel={periodLabel}
          {...(page.imageUrl ? { headerImageUrl: page.imageUrl } : {})}
          {...(showChannelNames && page.channelName
            ? { headerSubtitle: sanitizePdfDisplayText(page.channelName) }
            : {})}
          footerNote={t("cumulativeFooter")}
        >
          <CommercialEpisodesPdfSection page={page} />
        </MetricsPdfDeckPage>
      ))}
    </Document>
  );
}
